import React from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon } from '@/shared/assets/icons/chevron-down-icon';
import { ChevronUpIcon } from '@/shared/assets/icons/chevron-up-icon';
import { DEFlagIcon } from '@/shared/assets/icons/flags/de-flag-icon';
import { ESFlagIcon } from '@/shared/assets/icons/flags/es-flag-icon';
import { FIFlagIcon } from '@/shared/assets/icons/flags/fi-flag-icon';
import { FRFlagIcon } from '@/shared/assets/icons/flags/fr-flag-icon';
import { HUFlagIcon } from '@/shared/assets/icons/flags/hu-flag-icon';
import { ITFlagIcon } from '@/shared/assets/icons/flags/it-flag-icon';
import { JAFlagIcon } from '@/shared/assets/icons/flags/ja-flag-icon';
import { KOFlagIcon } from '@/shared/assets/icons/flags/ko-flag-icon';
import { NLFlagIcon } from '@/shared/assets/icons/flags/nl-flag-icon';
import { PLFlagIcon } from '@/shared/assets/icons/flags/pl-flag-icon';
import { PTFlagIcon } from '@/shared/assets/icons/flags/pt-flag-icon';
import { SKFlagIcon } from '@/shared/assets/icons/flags/sk-flag-icon';
import { SVFlagIcon } from '@/shared/assets/icons/flags/sv-flag-icon';
import { USFlagIcon } from '@/shared/assets/icons/flags/us-flag-icon';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import {
  LanguageSwitcherMenuItem,
  LanguageSwitcherSelect,
} from '@/shared/features/language-switcher/language-switcher.styled';
import {
  TLanguageOption,
  TLanguageSwitcherProps,
  UiLanguageEnum,
} from '@/shared/features/language-switcher/language-switcher.types';

const languageOptions: TLanguageOption[] = [
  {
    code: UiLanguageEnum.ENGLISH,
    flag: <USFlagIcon />,
    sortLabel: 'English',
  },
  {
    code: UiLanguageEnum.GERMAN,
    flag: <DEFlagIcon />,
    sortLabel: 'Deutsch',
  },
  {
    code: UiLanguageEnum.SPANISH,
    flag: <ESFlagIcon />,
    sortLabel: 'Español',
  },
  {
    code: UiLanguageEnum.PORTUGUESE,
    flag: <PTFlagIcon />,
    sortLabel: 'Português',
  },
  {
    code: UiLanguageEnum.ITALIAN,
    flag: <ITFlagIcon />,
    sortLabel: 'Italiano',
  },
  {
    code: UiLanguageEnum.FRENCH,
    flag: <FRFlagIcon />,
    sortLabel: 'Français',
  },
  {
    code: UiLanguageEnum.JAPANESE,
    flag: <JAFlagIcon />,
    sortLabel: '日本語',
  },
  {
    code: UiLanguageEnum.KOREAN,
    flag: <KOFlagIcon />,
    sortLabel: '한국어',
  },
  {
    code: UiLanguageEnum.DUTCH,
    flag: <NLFlagIcon />,
    sortLabel: 'Nederlands',
  },
  {
    code: UiLanguageEnum.FINNISH,
    flag: <FIFlagIcon />,
    sortLabel: 'Suomi',
  },
  {
    code: UiLanguageEnum.HUNGARIAN,
    flag: <HUFlagIcon />,
    sortLabel: 'Magyar',
  },
  {
    code: UiLanguageEnum.POLISH,
    flag: <PLFlagIcon />,
    sortLabel: 'Polski',
  },
  {
    code: UiLanguageEnum.SLOVAK,
    flag: <SKFlagIcon />,
    sortLabel: 'Slovenčina',
  },
  {
    code: UiLanguageEnum.SWEDISH,
    flag: <SVFlagIcon />,
    sortLabel: 'Svenska',
  },
];

const languageSortCollator = new Intl.Collator('en', { sensitivity: 'base' });

const languages = [...languageOptions].sort((left, right) =>
  languageSortCollator.compare(left.sortLabel, right.sortLabel),
);

export const LanguageSwitcher: React.FC<TLanguageSwitcherProps> = ({
  component,
  onLanguageChange,
  compact,
}) => {
  const { i18n, t } = useTranslation();

  const getSelectedLanguageIcon = () => {
    return languages.find((lang) => lang.code === i18n.language)?.flag;
  };

  const getTriggerComponent = (open: boolean) => {
    if (component === 'MenuItem') {
      return (
        <Tooltip
          title={t(`ui:language.${i18n.language}`)}
          disableHoverListener={!compact}
        >
          <LanguageSwitcherMenuItem
            icon={getSelectedLanguageIcon()}
            alignLabelLeft
          >
            {!compact && t(`ui:language.${i18n.language}`)}
          </LanguageSwitcherMenuItem>
        </Tooltip>
      );
    }

    return (
      <LanguageSwitcherSelect
        variant="secondary"
        size="large"
        icon={getSelectedLanguageIcon()}
        tooltip={t(`ui:language.${i18n.language}`)}
      >
        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </LanguageSwitcherSelect>
    );
  };

  return (
    <PopoverController
      trigger={(popoverState) => getTriggerComponent(popoverState)}
    >
      {(popoverClose) =>
        languages.map((lang) => (
          <MenuItem
            key={lang.code}
            value={lang.code}
            onClick={() => {
              void i18n.changeLanguage(lang.code);
              onLanguageChange?.(lang.code);
              popoverClose();
            }}
            selected={lang.code === i18n.language}
            icon={lang.flag}
          >
            {t(`ui:language.${lang.code}`)}
          </MenuItem>
        ))
      }
    </PopoverController>
  );
};
